import type { MouseEvent } from "react";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { examLocationWeekdays, type ExamLocation, type ExamLocationWeekday } from "../../model/ExamLocation";
import { examTypes } from "../../model/ExamType";
import { renderAddress } from "../../misc/AddressDisplay";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";

export function ExamLocationMobileListItem(props: {
  examLocation: ExamLocation;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}) {
  const { examLocation, onClick } = props;
  const theme = useTheme();
  const examTypesLabel = examLocation.types.map(_formatExamType).join(" · ");
  const practicalExamSchedule = _formatExamSchedule(
    examLocation.practicalExamDaysInAdvance,
    examLocation.practicalExamWeekdays,
  );
  const theoryExamSchedule = _formatExamSchedule(examLocation.theoryExamDaysInAdvance, examLocation.theoryExamWeekdays);

  return (
    <ButtonBase
      component="div"
      disabled={!onClick}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      sx={{
        alignItems: "stretch",
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
        display: "block",
        px: 0,
        py: 1,
        textAlign: "left",
        width: "100%",
        "&.Mui-disabled": {
          color: "inherit",
        },
      }}
    >
      <Column sx={{ gap: 0.5, minWidth: 0 }}>
        <Row sx={{ alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: theme.palette.action.hover,
              borderRadius: "50%",
              color: theme.palette.text.secondary,
              display: "flex",
              flexShrink: 0,
              height: 40,
              justifyContent: "center",
              width: 40,
            }}
          >
            <LocationOnOutlinedIcon fontSize="small" />
          </Box>
          <Column sx={{ gap: 0.25, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
              {examLocation.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
              {renderAddress(examLocation.postalAddress, { oneLine: true })}
            </Typography>
          </Column>
        </Row>
        {examTypesLabel && (
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {examTypesLabel}
          </Typography>
        )}
        {practicalExamSchedule && <ExamSchedule label="Praxis" schedule={practicalExamSchedule} />}
        {theoryExamSchedule && <ExamSchedule label="Theorie" schedule={theoryExamSchedule} />}
      </Column>
    </ButtonBase>
  );
}

function ExamSchedule(props: { label: string; schedule: string }) {
  const { label, schedule } = props;

  return (
    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}:
      </Box>{" "}
      {schedule}
    </Typography>
  );
}

function _formatExamSchedule(
  daysInAdvance: number | undefined,
  weekdays: ExamLocationWeekday[] | undefined,
): string | undefined {
  const scheduleParts = [
    daysInAdvance ? `${daysInAdvance} Tage im Voraus` : undefined,
    weekdays?.length ? weekdays.map(_formatExamLocationWeekday).join(", ") : undefined,
  ].filter(Boolean);

  return scheduleParts.length > 0 ? scheduleParts.join(" · ") : undefined;
}

function _formatExamLocationWeekday(weekday: keyof typeof examLocationWeekdays) {
  return examLocationWeekdays[weekday] || weekday;
}

function _formatExamType(examType: keyof typeof examTypes) {
  return examTypes[examType] || examType;
}
